
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TwitterDeliverable } from '@/types/twitterTypes';
import { useToast } from '@/hooks/use-toast';

export const useTwitterJobStatus = (deliverableId: string) => {
  const [jobStatus, setJobStatus] = useState<'none' | 'processing' | 'completed' | 'failed'>('none');
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deliverableDetails, setDeliverableDetails] = useState<TwitterDeliverable | null>(null);
  const { toast } = useToast();

  // Fetch the current deliverable details from the database
  const fetchDeliverableDetails = useCallback(async () => {
    if (!deliverableId) return;
    
    try {
      console.log(`Fetching deliverable details for: ${deliverableId}`);
      const { data, error } = await supabase
        .from('twitter_deliverables')
        .select('*')
        .eq('id', deliverableId)
        .single();
      
      if (error) {
        console.error('Error fetching deliverable details:', error);
        return null;
      }
      
      setDeliverableDetails(data as TwitterDeliverable);
      
      // Update local state based on the fetched data
      if (data) {
        setJobStatus(data.job_status as any || 'none');
        setProcessingProgress(data.processing_progress || 0);
        setErrorMessage(data.error_message || null);
      }
      
      return data as TwitterDeliverable;
    } catch (err) {
      console.error('Exception fetching deliverable details:', err);
      return null;
    }
  }, [deliverableId]);

  // Check the job status via the edge function
  const checkJobStatus = useCallback(async () => {
    if (!deliverableId) return;
    
    try {
      console.log(`Checking job status for deliverable: ${deliverableId}`);
      console.log(`Request body for status check: ${JSON.stringify({action: 'status', deliverableId})}`);
      
      const { data, error } = await supabase.functions.invoke('twitter-deliverables', {
        body: {
          action: 'status',
          deliverableId
        }
      });
      
      if (error) {
        console.error('Error checking job status:', error);
        return;
      }
      
      console.log('Status check response:', data);
      
      if (data) {
        // Update state with returned data
        setJobStatus(data.status);
        setProcessingProgress(data.progress || 0);
        setErrorMessage(data.error || null);
        
        // Re-fetch deliverable details if processing is complete or failed
        if (data.status === 'completed' || data.status === 'failed') {
          fetchDeliverableDetails();
        }
      }
      
      return data;
    } catch (err) {
      console.error('Exception checking job status:', err);
      setJobStatus('failed');
      setErrorMessage('Failed to check job status');
    }
  }, [deliverableId, fetchDeliverableDetails]);

  // Start a background processing job
  const startBackgroundProcessing = useCallback(async () => {
    if (!deliverableId) return false;
    
    try {
      const details = await fetchDeliverableDetails();
      
      if (!details) {
        console.error('Could not fetch deliverable details before processing');
        return false;
      }
      
      console.log(`Starting background processing for deliverable: ${deliverableId}`);
      console.log(`Request body for background processing: ${JSON.stringify({
        action: 'process', 
        deliverableId,
        query: details.content_query,
        startTime: details.start_date,
        endTime: details.end_date,
        type: details.type,
        kolUsernames: details.kol_usernames
      })}`);
      
      // First, update the deliverable status to indicate processing has started
      // This gives immediate feedback even if the edge function takes time
      const { error: updateError } = await supabase
        .from('twitter_deliverables')
        .update({
          job_status: 'processing',
          processing_progress: 0,
          error_message: null
        })
        .eq('id', deliverableId);
      
      if (updateError) {
        console.error('Error updating deliverable status:', updateError);
      }
      
      // Call the edge function to start processing
      const { data, error } = await supabase.functions.invoke('twitter-deliverables', {
        body: {
          action: 'process',
          deliverableId,
          query: details.content_query,
          startTime: details.start_date,
          endTime: details.end_date,
          type: details.type,
          kolUsernames: details.kol_usernames
        }
      });
      
      if (error) {
        console.error('Error starting background processing:', error);
        
        // Reset the status if the edge function call failed
        await supabase
          .from('twitter_deliverables')
          .update({
            job_status: 'failed',
            error_message: error.message || 'Edge function error'
          })
          .eq('id', deliverableId);
        
        setJobStatus('failed');
        setErrorMessage(error.message || 'Failed to start background processing');
        
        toast({
          title: 'Error',
          description: 'Failed to start background processing: ' + (error.message || 'Edge function error'),
          variant: 'destructive'
        });
        
        return false;
      }
      
      console.log('Background processing response:', data);
      
      if (data && data.message) {
        setJobStatus('processing');
        setProcessingProgress(0);
        toast({
          title: 'Processing Started',
          description: 'Background processing has started successfully.'
        });
        return true;
      } else {
        // Handle case where the edge function returns success: false
        console.error('Edge function returned error:', data?.error || 'Unknown error');
        
        await supabase
          .from('twitter_deliverables')
          .update({
            job_status: 'failed',
            error_message: data?.error || 'Unknown error'
          })
          .eq('id', deliverableId);
        
        setJobStatus('failed');
        setErrorMessage(data?.error || 'Failed to start background processing');
        
        toast({
          title: 'Error',
          description: data?.error || 'Failed to start background processing',
          variant: 'destructive'
        });
        
        return false;
      }
    } catch (err: any) {
      console.error('Exception starting background processing:', err);
      
      // Reset the status if we got an exception
      await supabase
        .from('twitter_deliverables')
        .update({
          job_status: 'failed',
          error_message: err.message || 'Unknown error'
        })
        .eq('id', deliverableId);
      
      setJobStatus('failed');
      setErrorMessage(err.message || 'Unknown error occurred');
      
      toast({
        title: 'Error',
        description: 'Failed to start background processing: ' + (err.message || 'Unknown error'),
        variant: 'destructive'
      });
      
      return false;
    }
  }, [deliverableId, fetchDeliverableDetails, toast]);
  
  // Perform a direct search (not background)
  const performDirectSearch = useCallback(async () => {
    if (!deliverableId) return false;
    
    try {
      const details = await fetchDeliverableDetails();
      
      if (!details) {
        console.error('Could not fetch deliverable details before direct search');
        return false;
      }
      
      console.log(`Performing direct search for deliverable: ${deliverableId}`);
      console.log(`Request body for direct search: ${JSON.stringify({
        action: 'search', 
        deliverableId,
        query: details.content_query,
        startTime: details.start_date,
        endTime: details.end_date,
        type: details.type,
        kolUsernames: details.kol_usernames
      })}`);
      
      toast({
        title: 'Direct Search',
        description: 'Performing direct search. This may take a moment...'
      });
      
      const { data, error } = await supabase.functions.invoke('twitter-deliverables', {
        body: {
          action: 'search',
          deliverableId,
          query: details.content_query,
          startTime: details.start_date,
          endTime: details.end_date,
          type: details.type,
          kolUsernames: details.kol_usernames
        }
      });
      
      if (error) {
        console.error('Error performing direct search:', error);
        toast({
          title: 'Error',
          description: 'Failed to perform direct search: ' + (error.message || 'Unknown error'),
          variant: 'destructive'
        });
        return false;
      }
      
      console.log('Direct search response:', data);
      
      if (data && data.success) {
        toast({
          title: 'Search Completed',
          description: `Found ${data.tweets_found || 0} tweets matching your criteria.`
        });
        
        // Refresh deliverable details
        fetchDeliverableDetails();
        return true;
      } else {
        toast({
          title: 'Error',
          description: data?.error || 'Failed to perform direct search',
          variant: 'destructive'
        });
        return false;
      }
    } catch (err: any) {
      console.error('Exception performing direct search:', err);
      toast({
        title: 'Error',
        description: 'Failed to perform direct search: ' + (err.message || 'Unknown error'),
        variant: 'destructive'
      });
      return false;
    }
  }, [deliverableId, fetchDeliverableDetails, toast]);
  
  // Cancel a processing job
  const cancelProcessing = useCallback(async () => {
    if (!deliverableId) return false;
    
    try {
      console.log(`Cancelling processing for deliverable: ${deliverableId}`);
      
      const { data, error } = await supabase.functions.invoke('twitter-deliverables', {
        body: {
          action: 'cancel',
          deliverableId
        }
      });
      
      if (error) {
        console.error('Error cancelling processing:', error);
        toast({
          title: 'Error',
          description: 'Failed to cancel processing: ' + (error.message || 'Unknown error'),
          variant: 'destructive'
        });
        return false;
      }
      
      console.log('Cancel processing response:', data);
      
      if (data && data.success) {
        setJobStatus('none');
        setProcessingProgress(0);
        toast({
          title: 'Processing Cancelled',
          description: 'Tweet processing has been cancelled.'
        });
        return true;
      } else {
        toast({
          title: 'Error',
          description: data?.error || 'Failed to cancel processing',
          variant: 'destructive'
        });
        return false;
      }
    } catch (err: any) {
      console.error('Exception cancelling processing:', err);
      toast({
        title: 'Error',
        description: 'Failed to cancel processing: ' + (err.message || 'Unknown error'),
        variant: 'destructive'
      });
      return false;
    }
  }, [deliverableId, toast]);

  // Initial fetch of deliverable details
  useEffect(() => {
    if (deliverableId) {
      fetchDeliverableDetails();
    }
  }, [deliverableId, fetchDeliverableDetails]);
  
  // Poll for status updates if job is processing
  useEffect(() => {
    if (!deliverableId) return;
    
    // Poll for status updates every 5 seconds when processing
    let interval: NodeJS.Timeout;
    
    if (jobStatus === 'processing') {
      interval = setInterval(() => {
        checkJobStatus();
      }, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [deliverableId, jobStatus, checkJobStatus]);

  return {
    jobStatus,
    processingProgress,
    errorMessage,
    deliverableDetails,
    fetchDeliverableDetails,
    checkJobStatus,
    startBackgroundProcessing,
    performDirectSearch,
    cancelProcessing
  };
};
